import React, { useEffect, useMemo, useState } from "react";
import { Container, Row, Col, Button, Input } from "reactstrap";
import { MaterialReactTable } from "material-react-table";
import { useDispatch, useSelector } from "react-redux";
import * as XLSX from "xlsx";

import { formatCurrency } from "../Utils/Currency";
import { getAllBPROne } from "../../slices/EVBusinessPerformanceReport/GetAllBPROne/thunk";
import { getAllBPRTwo } from "../../slices/EVBusinessPerformanceReport/GetAllBPRTwo/thunk";
import { Loader } from "../Utils/Loader";

/* ------------------------------ HELPERS ------------------------------ */

const getCurrentMonthStart = () => {
  const date = new Date();

  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(
    2,
    "0",
  )}-01`;
};

const getToday = () => {
  const date = new Date();

  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(
    2,
    "0",
  )}-${String(date.getDate()).padStart(2, "0")}`;
};

const formatValue = (value, decimalPlaces = 2) => {
  if (value === null || value === undefined || value === "") {
    return "-";
  }

  const number = Number(value);

  if (Number.isNaN(number)) {
    return value;
  }

  return number.toFixed(decimalPlaces);
};

const formatNumber = (value) => {
  if (value === null || value === undefined || value === "") {
    return "-";
  }

  const number = Number(value);

  if (Number.isNaN(number)) {
    return value;
  }

  return number.toLocaleString();
};

const formatUptime = (value) => {
  if (value === null || value === undefined || value === "") {
    return "-";
  }

  return `${Number(value).toFixed(2)}%`;
};

const normalizePartner = (partner) => {
  if (!partner) {
    return {
      partnerId: "",
      partnerName: "Unknown",
    };
  }

  let partnerId = partner.partnerId || "";
  let partnerName = partner.partnerName || "";

  if (!partnerName && partnerId.includes(" - ")) {
    const parts = partnerId.split(" - ");

    partnerId = parts.shift() || "";
    partnerName = parts.join(" - ") || "";
  }

  return {
    partnerId,
    partnerName: partnerName || "Unknown",
  };
};

/* ------------------------------ HEADER STYLE ------------------------------ */

const headStyle = {
  sx: {
    fontSize: "11px",
    fontWeight: 700,
    textTransform: "uppercase",
    color: "#111827",
    backgroundColor: "#fff",
    verticalAlign: "middle",
  },
};
const bodyStyle = {
  sx: {
    fontSize: "12px",
    fontWeight: 400,
    color: "#111827",
    whiteSpace: "normal",
    overflow: "visible",
    textOverflow: "clip",
    overflowWrap: "anywhere",
    wordBreak: "normal",
    py: "12px",

    "& *": {
      fontWeight: "400 !important",
    },
  },
};

const rightHead = {
  ...headStyle,
  sx: {
    ...headStyle.sx,

    "& .Mui-TableHeadCell-Content": {
      display: "flex",
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "flex-start",
    },

    "& .Mui-TableHeadCell-Content-Labels": {
      order: 1,
      flex: "0 0 auto",
    },

    "& .MuiTableSortLabel-root": {
      order: 2,
      marginLeft: "4px",
      marginRight: "0px",
    },

    "& .MuiTableSortLabel-icon": {
      marginLeft: "2px",
      marginRight: "0px",
    },
  },
};

const rightBody = {
  ...bodyStyle,
  sx: {
    ...bodyStyle.sx,
    fontWeight: 400,
    whiteSpace: "normal",
    overflow: "visible",
    textOverflow: "clip",
    overflowWrap: "anywhere",
    wordBreak: "normal",
  },
};

/* ------------------------------ COMPONENT ------------------------------ */

function EVBusinessPerformanceReport() {
  const dispatch = useDispatch();

  const {
    stationConnectorSummary = {
      totalStations: 0,
      totalConnectors: 0,
      totalStationsOnboard: 0,
      partners: [],
    },
    loading: bprOneLoading = false,
  } = useSelector((state) => state.GetAllBPROne || {});

  const {
    revenueSummary = {
      grossRevenue: 0,
      netRevenue: 0,
      averageRevenuePerStation: 0,
      partners: [],
    },

    uniqueCustomers = {
      totalUniqueCustomers: 0,
      partners: [],
    },

    sessionUptime = {
      totalUpTime: 0,
      uptimePercentage: 0,
      partners: [],
    },

    sessionSummary = {
      totalSessions: 0,
      totalKWh: 0,
      partners: [],
    },

    loading: bprTwoLoading = false,
  } = useSelector((state) => state.GetAllBPRTwo || {});

  const [searchText, setSearchText] = useState("");

  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 30,
  });

  const [fromDate, setFromDate] = useState(getCurrentMonthStart());
  const [toDate, setToDate] = useState(getToday());

  const loading = bprOneLoading || bprTwoLoading;

  useEffect(() => {
    const startDate = fromDate ? `${fromDate}T00:00:00Z` : "";
    const endDate = toDate ? `${toDate}T23:59:59Z` : "";

    dispatch(
      getAllBPROne(
        pagination.pageSize,
        pagination.pageIndex * pagination.pageSize,
        startDate,
        endDate,
      ),
    );

    dispatch(
      getAllBPRTwo(
        pagination.pageSize,
        pagination.pageIndex * pagination.pageSize,
        startDate,
        endDate,
      ),
    );
  }, [dispatch, fromDate, toDate, pagination.pageIndex, pagination.pageSize]);

  /* ------------------------------ TABLE DATA ------------------------------ */

  const data = useMemo(() => {
    const partnerMap = new Map();

    const normalizePartner = (partner) => {
      if (!partner) {
        return {
          partnerId: "",
          partnerName: "",
        };
      }

      let partnerId = partner.partnerId || "Blueocean";
      let partnerName = partner.partnerName || "";

      if (!partnerName && partnerId.includes(" - ")) {
        const parts = partnerId.split(" - ");

        partnerId = parts.shift() || "";
        partnerName = parts.join(" - ") || "";
      }

      return {
        partnerId,
        partnerName,
      };
    };

    /* ---------------- CREATE / GET PARTNER ---------------- */

    const ensurePartner = (partner) => {
      const normalized = normalizePartner(partner);

      const key = normalized.partnerId || normalized.partnerName || "unknown";

      if (!partnerMap.has(key)) {
        partnerMap.set(key, {
          /* Existing table fields */

          period: `${fromDate} to ${toDate}`,

          partner: normalized.partnerName
            ? `${normalized.partnerId} - ${normalized.partnerName}`
            : normalized.partnerId || "-",

          // stations: 0,
          // connectors: 0,
          // newStations: 0,

          // sessions: 0,
          // energy: 0,
          // users: 0,

          // gross: 0,
          // net: 0,
          // avgRev: 0,

          // uptime: 0,

          /* Internal fields */

          partnerId: normalized.partnerId,
          partnerName: normalized.partnerName,
        });
      }

      return partnerMap.get(key);
    };

    /* ============================================================
     1. STATION / CONNECTOR SUMMARY
     ============================================================ */

    (stationConnectorSummary?.partners || []).forEach((partner) => {
      const row = ensurePartner(partner);

      if (!row) return;

      row.totalStations = Number(partner.totalStations || 0);
      row.totalConnectors = Number(partner.totalConnectors || 0);
      row.totalStationsOnboard = Number(partner.totalStationsOnboard || 0);
    });

    /* ============================================================
     2. UNIQUE CUSTOMERS
     ============================================================ */

    (uniqueCustomers?.partners || []).forEach((partner) => {
      const row = ensurePartner(partner);

      if (!row) return;

      row.users = Number(partner.uniqueCustomerCount || 0);
    });

    /* ============================================================
     3. SESSION UPTIME
     ============================================================ */

    (sessionUptime?.partners || []).forEach((partner) => {
      const row = ensurePartner(partner);

      if (!row) return;

      row.uptime = Number(partner.uptimePercentage || 0);
    });

    /* ============================================================
     4. SESSION SUMMARY
     ============================================================ */

    (sessionSummary?.partners || []).forEach((partner) => {
      const row = ensurePartner(partner);

      if (!row) return;

      row.sessions = Number(partner.sessionCount || 0);

      row.energy = Number(partner.totalKWh || 0);
    });

    /* ============================================================
     5. REVENUE SUMMARY
     ============================================================ */

    (revenueSummary?.partners || []).forEach((partner) => {
      const row = ensurePartner(partner);

      if (!row) return;

      row.gross = Number(partner.grossRevenue || 0);

      row.net = Number(partner.netRevenue || 0);

      row.avgRev = Number(partner.averageRevenuePerStation || 0);
    });

    /* ============================================================
     SEARCH
     ============================================================ */

    return Array.from(partnerMap.values()).filter((row) => {
      if (!searchText.trim()) {
        return true;
      }

      const search = searchText.trim().toLowerCase();

      return (
        row.partner?.toLowerCase().includes(search) ||
        row.partnerId?.toLowerCase().includes(search) ||
        row.partnerName?.toLowerCase().includes(search)
      );
    });
  }, [
    stationConnectorSummary,
    uniqueCustomers,
    sessionUptime,
    sessionSummary,
    revenueSummary,
    searchText,
    fromDate,
    toDate,
  ]);

  /* ------------------------------ SUMMARY ------------------------------ */

  const summary = useMemo(
    () => [
      {
        label: "TOTAL STATIONS",
        value: formatNumber(stationConnectorSummary?.totalStations),
        note: "in selected period",
      },
      {
        label: "TOTAL CONNECTORS",
        value: formatNumber(stationConnectorSummary?.totalConnectors),
        note: "available connectors",
      },
      {
        label: "STATIONS ONBOARDED",
        value: formatNumber(stationConnectorSummary?.totalStationsOnboard),
        note: "onboarded stations",
      },
      {
        label: "TOTAL SESSIONS",
        value: formatNumber(sessionSummary?.totalSessions),
        note: "charging sessions",
      },
      {
        label: "TOTAL ENERGY",
        value: `${formatValue(sessionSummary?.totalKWh, 2)} kWh`,
        note: "energy delivered",
      },
      {
        label: "GROSS REVENUE",
        value: formatCurrency(revenueSummary?.grossRevenue || 0),
        note: "gross revenue",
      },
      {
        label: "NET REVENUE",
        value: formatCurrency(revenueSummary?.netRevenue || 0),
        note: "net revenue",
      },
      {
        label: "UNIQUE CUSTOMERS",
        value: formatNumber(uniqueCustomers?.totalUniqueCustomers),
        note: "unique customers",
      },
    ],
    [stationConnectorSummary, sessionSummary, revenueSummary, uniqueCustomers],
  );

  /* ------------------------------ RESET ------------------------------ */

  const handleReset = () => {
    setFromDate(getCurrentMonthStart());
    setToDate(getToday());
    setSearchText("");

    setPagination((p) => ({
      ...p,
      pageIndex: 0,
    }));
  };

  const handleDownload = () => {
    if (!data || data.length === 0) {
      return;
    }

    const headers = [
      "Period",
      "Partner ID - Name",
      "Total Stations / Connectors",
      "New Stations Added",
      "Total Sessions",
      "Total Energy Delivered (kWh)",
      "Total Unique Active Users",
      "Total Gross Revenue",
      "Total Net Revenue",
      "Average Revenue Per Station (NET)",
      "Network-wide Uptime (%)",
    ];

    // Only export actual data rows
    const rows = data
      .filter((r) => r && r.partner)
      .map((r) => [
        r.period || "-",
        r.partner || "-",
        `${r.totalStations ?? 0} / ${r.totalConnectors ?? 0}`,
        r.newStations ?? 0,
        r.sessions ?? 0,
        formatValue(r.energy, 2),
        r.users ?? 0,
        formatCurrency(r.gross ?? 0),
        formatCurrency(r.net ?? 0),
        formatCurrency(r.avgRev ?? 0),
        `${Number(r.uptime ?? 0).toFixed(1)}%`,
      ]);

    // Do not export if there are no valid rows
    if (rows.length === 0) {
      return;
    }

    // Create worksheet
    const worksheet = XLSX.utils.aoa_to_sheet([headers, ...rows]);

    // Column widths
    worksheet["!cols"] = [
      { wch: 24 },
      { wch: 35 },
      { wch: 28 },
      { wch: 22 },
      { wch: 18 },
      { wch: 30 },
      { wch: 28 },
      { wch: 22 },
      { wch: 22 },
      { wch: 35 },
      { wch: 25 },
    ];

    // Header style
    const headerStyle = {
      font: {
        bold: true,
        color: "000000",
        sz: 12,
      },
      fill: {
        patternType: "solid",
        fgColor: { rgb: "FFFFFF" },
      },
      alignment: {
        horizontal: "center",
        vertical: "center",
        wrapText: true,
      },
      border: {
        top: {
          style: "thin",
          color: { rgb: "A6A6A6" },
        },
        bottom: {
          style: "thin",
          color: { rgb: "A6A6A6" },
        },
        left: {
          style: "thin",
          color: { rgb: "A6A6A6" },
        },
        right: {
          style: "thin",
          color: { rgb: "A6A6A6" },
        },
      },
    };

    // Body style
    const bodyStyle = {
      font: {
        color: "000000",
        sz: 11,
      },
      alignment: {
        vertical: "center",
        horizontal: "left",
      },
      border: {
        top: {
          style: "thin",
          color: { rgb: "A6A6A6" },
        },
        bottom: {
          style: "thin",
          color: { rgb: "A6A6A6" },
        },
        left: {
          style: "thin",
          color: { rgb: "A6A6A6" },
        },
        right: {
          style: "thin",
          color: { rgb: "A6A6A6" },
        },
      },
    };

    // Apply header styles
    headers.forEach((_, colIndex) => {
      const cellAddress = XLSX.utils.encode_cell({
        r: 0,
        c: colIndex,
      });

      if (worksheet[cellAddress]) {
        worksheet[cellAddress].s = headerStyle;
      }
    });

    // Apply body styles
    rows.forEach((row, rowIndex) => {
      row.forEach((_, colIndex) => {
        const cellAddress = XLSX.utils.encode_cell({
          r: rowIndex + 1,
          c: colIndex,
        });

        if (worksheet[cellAddress]) {
          worksheet[cellAddress].s = bodyStyle;
        }
      });
    });

    // Create workbook
    const workbook = XLSX.utils.book_new();

    XLSX.utils.book_append_sheet(workbook, worksheet, "Business Performance");

    // Download XLSX
    XLSX.writeFile(
      workbook,
      `Business_Performance_Report_${fromDate}_to_${toDate}.xlsx`,
    );
  };

  /* ------------------------------ COLUMNS ------------------------------ */

  const columns = useMemo(
    () => [
      {
        accessorKey: "period",
        header: "Period",
        size: 210,
        sortingFn: (a, b) =>
          a.original.periodStart?.localeCompare(b.original.periodStart || ""),
      },

      {
        accessorKey: "partner",
        header: "Partner ID - Name",
        size: 220,
      },

      {
        accessorKey: "stations",
        header: "Total Stations / Connectors",
        size: 190,
        muiTableHeadCellProps: rightHead,
        muiTableBodyCellProps: rightBody,
        Cell: ({ row }) =>
          `${row.original.totalStations} / ${row.original.totalConnectors}`,
      },

      {
        accessorKey: "newStations",
        header: "New Stations Added",
        size: 190,
        muiTableHeadCellProps: rightHead,
        muiTableBodyCellProps: rightBody,
        Cell: ({ row }) => `${row.original.totalStationsOnboard}`,
      },

      {
        accessorKey: "sessions",
        header: "Total Sessions",
        size: 160,
        muiTableHeadCellProps: rightHead,
        muiTableBodyCellProps: rightBody,
        Cell: ({ row }) => `${row.original.sessions || "-"}`,
      },

      {
        accessorKey: "uptime",
        header: "Network-wide Uptime (%)",
        size: 160,
        muiTableHeadCellProps: rightHead,
        muiTableBodyCellProps: rightBody,
        Cell: ({ cell }) => {
          const value = cell.getValue();

          return value === null || value === undefined || value === ""
            ? "-"
            : `${Number(value).toFixed(2)}%`;
        },
      },

      {
        accessorKey: "energy",
        header: "Total Energy Delivered (kWh)",
        size: 170,
        muiTableHeadCellProps: rightHead,
        muiTableBodyCellProps: rightBody,
        Cell: ({ cell }) => {
          const value = cell.getValue();
          return value === null || value === undefined || value === ""
            ? "-"
            : Number(value).toFixed(2);
        },
      },

      {
        accessorKey: "users",
        header: "Total Unique Active Users",
        size: 170,
        muiTableHeadCellProps: rightHead,
        muiTableBodyCellProps: rightBody,
        Cell: ({ row }) => `${row.original.users || "-"}`,
      },

      {
        accessorKey: "gross",
        header: "Total Gross Revenue",
        size: 200,
        muiTableHeadCellProps: rightHead,
        muiTableBodyCellProps: rightBody,
        Cell: ({ cell }) => {
          const value = cell.getValue();

          return value === null ||
            value === undefined ||
            value === "" ||
            !Number.isFinite(Number(value))
            ? "-"
            : formatCurrency(Number(value).toFixed(2));
        },
      },

      {
        accessorKey: "net",
        header: "Total Net Revenue",
        size: 190,
        muiTableHeadCellProps: rightHead,
        muiTableBodyCellProps: rightBody,
        Cell: ({ cell }) => {
          const value = cell.getValue();

          return value === null ||
            value === undefined ||
            value === "" ||
            !Number.isFinite(Number(value))
            ? "-"
            : formatCurrency(Number(value).toFixed(2));
        },
      },

      {
        accessorKey: "avgRev",
        header: "Average Revenue Per Station (NET)",
        size: 170,
        muiTableHeadCellProps: rightHead,
        muiTableBodyCellProps: rightBody,
        Cell: ({ cell }) => {
          const value = cell.getValue();

          return value === null ||
            value === undefined ||
            value === "" ||
            !Number.isFinite(Number(value))
            ? "-"
            : formatCurrency(Number(value).toFixed(2));
        },
      },
    ],
    [],
  );

  /* ------------------------------ RETURN ------------------------------ */

  return (
    <div id="bpr">
      <Container fluid>
        <Row>
          <Col xs={12}>
            <h5 className="mb-3 fw-medium" style={{ fontSize: "15px" }}>
              Monitor Business Performance, Revenue and Charging Metrics.
            </h5>
          </Col>

          {/* Filters */}

          <Col xs={12}>
            <div
              className="d-flex flex-column flex-md-row align-items-md-end gap-3 border p-3 mb-3"
              style={{
                borderRadius: "10px",
                width: "100%",
                maxWidth: "100%",
              }}
            >
              {/* Date Filters */}
              <div
                className="d-flex gap-3"
                style={{
                  flex: 1,
                  minWidth: 0,
                }}
              >
                <div style={{ minWidth: "130px", flex: 1 }}>
                  <label
                    className="fw-semibold mb-1"
                    style={{ fontSize: "11px" }}
                  >
                    FROM DATE
                  </label>

                  <Input
                    type="date"
                    value={fromDate}
                    // min={getCurrentMonthStart()}
                    max={toDate || getToday()}
                    onChange={(e) => {
                      setFromDate(e.target.value);

                      setPagination((p) => ({
                        ...p,
                        pageIndex: 0,
                      }));
                    }}
                    style={{
                      height: "36px",
                      borderRadius: "4px",
                      fontSize: "12px",
                      width: "100%",
                    }}
                  />
                </div>

                <div style={{ minWidth: "130px", flex: 1 }}>
                  <label
                    className="fw-semibold mb-1"
                    style={{ fontSize: "11px" }}
                  >
                    TO DATE
                  </label>

                  <Input
                    type="date"
                    value={toDate}
                    min={fromDate}
                    max={getToday()}
                    onChange={(e) => {
                      setToDate(e.target.value);

                      setPagination((p) => ({
                        ...p,
                        pageIndex: 0,
                      }));
                    }}
                    style={{
                      height: "36px",
                      borderRadius: "4px",
                      fontSize: "12px",
                      width: "100%",
                    }}
                  />
                </div>
              </div>

              {/* Search */}
              <div
                style={{
                  minWidth: "260px",
                  flex: 2,
                }}
              >
                <label
                  className="fw-semibold mb-1"
                  style={{ fontSize: "11px" }}
                >
                  SEARCH
                </label>

                <div className="position-relative">
                  <i
                    className="ri-search-line position-absolute text-muted"
                    style={{
                      left: "10px",
                      top: "50%",
                      transform: "translateY(-50%)",
                    }}
                  />

                  <Input
                    type="text"
                    placeholder="Partner ID, Name, ..."
                    value={searchText}
                    onChange={(e) => {
                      setSearchText(e.target.value);
                      resetPage();
                    }}
                    style={{
                      height: "36px",
                      borderRadius: "6px",
                      fontSize: "12px",
                      paddingLeft: "32px",
                      width: "100%",
                    }}
                  />
                </div>
              </div>

              {/* Buttons */}
              <div className="d-flex gap-2 flex-shrink-0">
                <Button
                  color="light"
                  className="border rounded-1 text-nowrap fw-semibold d-flex align-items-center justify-content-center"
                  style={{
                    height: "36px",
                    fontSize: "12px",
                  }}
                  onClick={handleReset}
                >
                  <i className="ri-refresh-line me-1" />
                  Reset
                </Button>

                <Button
                  className="border rounded-1 text-nowrap fw-semibold d-flex align-items-center justify-content-center"
                  style={{
                    height: "36px",
                    fontSize: "12px",
                  }}
                  onClick={handleDownload}
                >
                  <i className="ri-download-2-line me-1" />
                  Download Excel
                </Button>
              </div>
            </div>
          </Col>

          {/* Summary Cards */}

          {/* 
          {summary.map((c) => (
            <Col
              key={c.label}
              xs={6}
              lg={3}
              xl={2}
              className="mb-3"
            >
              <div
                className="border p-3 h-100"
                style={{ borderRadius: "8px" }}
              >
                <div
                  className="fw-semibold mb-1"
                  style={{
                    fontSize: "10px",
                    letterSpacing: ".05em",
                  }}
                >
                  {c.label}
                </div>

                <div
                  className="fw-bold"
                  style={{
                    fontSize: "20px",
                    lineHeight: 1.2,
                  }}
                >
                  {c.value}
                </div>

                <div
                  className="text-muted mt-1"
                  style={{ fontSize: "10px" }}
                >
                  {c.note}
                </div>
              </div>
            </Col>
          ))}
          */}

          {/* Table */}

          <Col lg={12}>
            <div
              className="border table-responsive"
              style={{ borderRadius: "4px" }}
            >
              <div>
                <MaterialReactTable
                  columns={columns}
                  data={data}
                  state={{
                    pagination,
                  }}
                  onPaginationChange={setPagination}
                  manualPagination
                  rowCount={data.length}
                  columnResizeMode="on"
                  defaultColumn={{
                    minSize: 40,
                    maxSize: 1000,
                    size: 150,
                  }}
                  muiTableHeadCellProps={headStyle}
                  muiTableBodyCellProps={{
                    ...bodyStyle,
                    sx: {
                      ...bodyStyle.sx,
                      fontWeight: 400,
                      whiteSpace: "normal",
                      overflow: "visible",
                      textOverflow: "clip",
                      overflowWrap: "anywhere",
                      "& *": {
                        fontWeight: "400 !important",
                      },
                    },
                  }}
                  muiTableProps={{
                    sx: {
                      tableLayout: "fixed",
                      width: "100%",
                      minWidth: "2400px",

                      "& td": {
                        fontWeight: "400 !important",
                        whiteSpace: "normal",
                        overflow: "visible",
                        textOverflow: "clip",
                        overflowWrap: "anywhere",
                      },
                    },
                  }}
                  muiTableBodyRowProps={{
                    sx: {
                      "&:nth-of-type(even) > td": {
                        backgroundColor: "#F9FAFB",
                      },
                    },
                  }}
                  muiPaginationProps={{
                    rowsPerPageOptions: [10, 25, 50, 75, 100],
                    showRowsPerPage: true,
                  }}
                  enableTopToolbar={false}
                  enableDensityToggle={false}
                  enableColumnActions={false}
                  enableFullScreenToggle={false}
                  enableColumnFilters={false}
                  enableSorting
                  muiTablePaperProps={{
                    elevation: 0,
                    sx: {
                      borderRadius: "0px",
                      boxShadow: "none",
                    },
                  }}
                />
              </div>
            </div>
          </Col>
        </Row>
      </Container>
      {loading && <Loader />}
    </div>
  );
}

export default EVBusinessPerformanceReport;
